/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { IAdminExpressionData } from '@datatype/admin/api-type';
import { ISegment, ISourceSegment } from '@datatype/iyagigo/api-type';
import TextBlock from '@component/common/block/text-block';

interface IProps {
  expression: IAdminExpressionData;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  padding: 12px;
  background-color: ${color.GRAY_5};
  border-radius: 8px;
`;

const KeyBox = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 4px;
  padding: 10px 12px;
  background-color: ${color.BROWN_4};
  border: 1px solid ${color.BROWN_1};
  border-radius: 6px;
`;

const SegmentRow = styled.div<{ $isKey: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  gap: 2px;
  padding: 8px 10px;
  background-color: ${color.WHITE_1};
  border-radius: 6px;
  border: 2px solid ${(p) => (p.$isKey ? color.YELLOW_2 : 'transparent')};
`;

const ExpressionDialogue: React.FC<IProps> = ({ expression }) => {
  const sourceSegments: ISourceSegment[] = expression.sourceSegment ?? [];
  const segments: ISegment[] = expression.segmentKo ?? [];
  const substantive = sourceSegments.filter((s) => s.substantive);
  const sourceKey = expression.sourceKeySegment;
  const keyKo = expression.keySegmentKo;

  if (substantive.length === 0 && !sourceKey) {
    return (
      <Wrapper>
        <TextBlock text='대화 정보가 없습니다.' design={{ text: { size: 'small', color: color.BLACK_5 } }} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {sourceKey && (
        <KeyBox>
          <TextBlock
            text='핵심표현'
            design={{ text: { size: 'small', color: color.BROWN_1, bold: true } }}
          />
          <TextBlock
            text={sourceKey.keyExpressionTemplate}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
          />
          {keyKo?.keyExpressionTranslation && (
            <TextBlock
              text={keyKo.keyExpressionTranslation}
              design={{ text: { size: 'small', color: color.BLACK_5 } }}
            />
          )}
        </KeyBox>
      )}
      {substantive.map((src) => {
        const seg = segments.find((s) => s.index === src.index);
        const isKey = sourceKey?.index === src.index;
        return (
          <SegmentRow key={src.index} $isKey={isKey}>
            <TextBlock
              text={src.text}
              design={{ text: { size: 'medium', color: color.BLACK_2 } }}
            />
            {seg?.translation && (
              <TextBlock
                text={seg.translation}
                design={{ text: { size: 'small', color: color.BLACK_5 } }}
              />
            )}
          </SegmentRow>
        );
      })}
    </Wrapper>
  );
};

export default ExpressionDialogue;
